import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { ChatListSidebarItem } from '../../conversation_list/schema'
import deleteAgentConversation from '@/agent/conversations/fetch/delete-hide-agent-conversation'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { useNavigate, useParams } from 'react-router-dom'
import { RootState } from '@/store/store'
import { buildChatFeaturePath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { ModalFooterWithActionsType } from '@/components/modal/constants'

type ConversationDeleteConfirmDialogProps = {
  dialogVisible: boolean
  chatItem: ChatListSidebarItem | null
  onDialogClose: () => void
}

export default function ConversationDeleteConfirmDialog(props: ConversationDeleteConfirmDialogProps) {
  const { dialogVisible, chatItem, onDialogClose } = props
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { chatFeature, chatId } = useParams()
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)

  const handleConfirm = async () => {
    setLoading(true)
    setError(false)

    if (!chatItem) {
      console.error('No conversation to delete.')
      return
    }

    try {
      await deleteAgentConversation(chatItem.id)
      dispatch(AgentConversationsActions.deleteConversation({ conversationId: chatItem.id }))

      // If the conversation being deleted is currently being viewed, navigate away
      if (chatId === chatItem.id && chatFeature && activeMatterId) {
        navigate(buildChatFeaturePath(activeMatterId))
      }

      // Short delay before closing the dialog
      await new Promise((resolve) => setTimeout(resolve, 500))

      onDialogClose()
    } catch (err) {
      console.error('Error while deleting conversation:', err)
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setError(false)
      onDialogClose()
    }
  }

  return (
    <Modal open={dialogVisible} onOpenChange={handleOpenChange}>
      <ModalContent
        title="Delete Conversation?"
        subtitle={`You are about to permanently delete "${chatItem?.title}". Please confirm.`}
        width="md"
        height="max-md"
        footer={
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            secondaryAction={{ label: 'Cancel', onClick: () => handleOpenChange(false), disabled: loading }}
            primaryAction={{ label: 'Delete', onClick: handleConfirm, loading }}
            inlineMessage={error ? { variant: 'error', children: 'Failed to delete chat. Please try again.' } : undefined}
          />
        }
      />
    </Modal>
  )
}
